import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api, ErrorDeApi, type ControlDeEnvioDelDespacho, type EstadoDeEnvio, type PendienteDeCliente, type PendientesDePresentacion,
} from '../servicios/api'
import {
  BannerDeRechazo, type ChipDePlazo, FranjaDePlazos, GRUPOS_DE_ARCHIVO, plazosDelPeriodo, SelloXsd,
} from './ControlDeEnvios'

const NOMBRE_DE_TIPO_ARCHIVO: Record<ControlDeEnvioDelDespacho['tipoArchivo'], string> = {
  Ct: 'CT · Catálogo',
  Bn: 'BN · Balanza',
  Bc: 'BC · Balanza compl.',
  Pl: 'PL · Pólizas',
  Xf: 'XF · Aux. folios',
  Xc: 'XC · Aux. cuentas',
}

const INSIGNIA_DE_ESTADO: Record<EstadoDeEnvio, string> = {
  Generado: 'aviso',
  Enviado: 'aviso',
  Aceptado: 'vigente',
  Rechazado: 'vencido',
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es-MX') : '—')

/**
 * Lo que falta para un envío: Generado y Enviado siguen abiertos; Rechazado también, porque hay
 * que rehacerlo. Solo Aceptado cierra.
 */
export function faltaAlgo(estado: EstadoDeEnvio): boolean {
  return estado !== 'Aceptado'
}

/**
 * RF-1608 · En qué está cada cliente respecto de lo que hay que presentar del periodo. Cuatro estados, y no dos:
 * un cliente sin nada que presentar porque todavía no se genera nada NO está «al corriente» (regla de honestidad:
 * un cero sin su explicación miente).
 */
export function estadoDePresentacion(p: PendienteDeCliente): { etiqueta: string; insignia: string } {
  if (p.hayPaquete) return { etiqueta: 'Por presentar', insignia: 'aviso' }
  if (p.pendientesDeGenerar.length > 0) return { etiqueta: 'Falta generar', insignia: 'aviso' }
  if (p.acusesPendientes > 0) return { etiqueta: 'Esperando acuse', insignia: 'aviso' }
  return { etiqueta: 'Al corriente', insignia: 'vigente' }
}

/** Por qué no hay paquete que descargar, dicho a quien ve el botón deshabilitado. */
function porQueNoHayPaquete(p: PendienteDeCliente): string {
  if (p.pendientesDeGenerar.length > 0) return 'Primero genera lo que falta: no hay nada listo para presentar.'
  if (p.acusesPendientes > 0) return 'Ya se presentó todo; solo faltan acuses por registrar.'
  return 'No hay nada pendiente de presentar en este periodo.'
}

/**
 * RF-1608 · «¿Qué me falta presentar?» de toda la cartera en el periodo: archivos del Anexo 24 sin enviar o
 * rechazados, declaraciones con papel sellado y sin presentar, lo que falta generar y los acuses pendientes,
 * con la descarga del paquete del periodo de cada cliente (RF-1607) sin entrar a su expediente.
 */
export function QueMeFaltaPresentar({ anio, mes }: { anio: number; mes: number }) {
  const [pendientes, setPendientes] = useState<PendientesDePresentacion>()
  const [error, setError] = useState('')
  const [descargando, setDescargando] = useState<string>()
  const [errores, setErrores] = useState<Record<string, string>>({})

  useEffect(() => {
    let vigente = true
    setPendientes(undefined)
    setError('')
    setErrores({})
    api.pendientesDePresentacion(anio, mes)
      .then((r) => { if (vigente) setPendientes(r) })
      .catch((e) => {
        if (vigente) setError(e instanceof ErrorDeApi ? e.message : 'No se pudo saber qué falta presentar en el periodo.')
      })
    return () => { vigente = false }
  }, [anio, mes])

  async function descargar(clienteId: string) {
    setDescargando(clienteId)
    setErrores(({ [clienteId]: _anterior, ...resto }) => resto)
    try {
      await api.descargarPaqueteDePresentacion(clienteId, anio, mes)
    } catch (e) {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo generar el paquete de presentación.'
      setErrores((actuales) => ({ ...actuales, [clienteId]: mensaje }))
    } finally {
      setDescargando(undefined)
    }
  }

  const periodo = `${MESES[mes - 1]} ${anio}`

  return (
    <section aria-labelledby="titulo-que-falta" style={{ marginTop: 'var(--space-4)' }}>
      <h2 id="titulo-que-falta">Qué me falta presentar</h2>
      {error && <p className="error" role="alert">{error}</p>}
      {!error && !pendientes && <p>Cargando…</p>}
      {pendientes && pendientes.clientes.length === 0 && (
        <p className="vacio">No tienes clientes activos en tu cartera, así que no hay nada que presentar en {periodo}.</p>
      )}
      {pendientes && pendientes.clientes.length > 0 && (
        <>
          <p className="sutil">
            {pendientes.clientes.filter((p) => p.hayPaquete).length} de {pendientes.clientes.length}{' '}
            {pendientes.clientes.length === 1 ? 'cliente tiene' : 'clientes tienen'} algo listo para presentar de {periodo}.
            El paquete trae los archivos del Anexo 24, los papeles de las declaraciones y la guía de cada uno.
          </p>
          <div className="tabla-envoltura">
            <table className="tabla" aria-label={`Qué falta presentar de ${periodo}`}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Anexo 24</th>
                  <th>Declaraciones</th>
                  <th>Falta generar</th>
                  <th>Acuses pendientes</th>
                  <th>Paquete</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.clientes.map((p) => {
                  const estado = estadoDePresentacion(p)
                  const idDelPorque = `porque-${p.clienteId}`
                  return (
                    <tr key={p.clienteId}>
                      <td>
                        <Link to={`/clientes/${p.clienteId}/control-envios`}>{p.razonSocial}</Link>{' '}
                        <span className="sutil mono">{p.rfc}</span>
                      </td>
                      <td><span className={`insignia ${estado.insignia}`}>{estado.etiqueta}</span></td>
                      <td>
                        {p.archivos.length === 0
                          ? <span className="sutil">—</span>
                          : p.archivos.map((a) => (
                            <div key={a.controlId} style={{ whiteSpace: 'nowrap' }}>
                              {NOMBRE_DE_TIPO_ARCHIVO[a.tipoArchivo]}
                              {a.estado === 'Rechazado' && <> <span className="insignia vencido">rechazado</span></>}
                            </div>
                          ))}
                      </td>
                      <td>{p.declaraciones.length === 0 ? <span className="sutil">—</span> : p.declaraciones.join(', ')}</td>
                      <td>
                        {p.pendientesDeGenerar.length === 0
                          ? <span className="sutil">—</span>
                          : p.pendientesDeGenerar.map((texto) => <div key={texto}>{texto}</div>)}
                      </td>
                      <td className="num">{p.acusesPendientes === 0 ? <span className="sutil">—</span> : p.acusesPendientes}</td>
                      <td>
                        <button type="button" className="secundario" disabled={!p.hayPaquete || descargando === p.clienteId}
                          aria-describedby={p.hayPaquete ? undefined : idDelPorque}
                          onClick={() => void descargar(p.clienteId)}>
                          {descargando === p.clienteId ? 'Generando…' : 'Descargar paquete'}
                        </button>
                        {!p.hayPaquete && <div id={idDelPorque} className="sutil" style={{ fontSize: 12 }}>{porQueNoHayPaquete(p)}</div>}
                        {errores[p.clienteId] && <div className="error" role="alert">{errores[p.clienteId]}</div>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}

/**
 * Contabilidad electrónica de TODO el despacho (A1, RF-805). La pantalla por cliente responde
 * «¿cómo va este?»; el cierre mensual pregunta «¿quién me falta?», y hasta ahora eso obligaba a
 * entrar cliente por cliente.
 */
export function ContabilidadElectronica() {
  const hoy = new Date()
  // Arranca en el mes pasado: la contabilidad electrónica de un periodo se manda cuando el
  // periodo ya terminó, así que el mes en curso casi siempre estaría vacío.
  const mesAnterior = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)
  const [anio, setAnio] = useState(mesAnterior.getFullYear())
  const [mes, setMes] = useState(mesAnterior.getMonth() + 1)
  const [envios, setEnvios] = useState<ControlDeEnvioDelDespacho[]>()
  const [error, setError] = useState('')
  // A4 · El mismo segmentado que la pantalla por cliente, con los mismos cuatro grupos: el
  // contador busca «la balanza» o «lo que pidió el SAT», no seis tipos sueltos.
  const [grupo, setGrupo] = useState<string>()
  // A2 · Tres estados a propósito: `undefined` es «todavía no pregunté», `'desconocido'` es
  // «pregunté y falló». Pintar la franja vacía en el segundo caso diría «no hay plazo», que es
  // justo lo contrario de lo que pasa.
  const [plazos, setPlazos] = useState<ChipDePlazo[] | 'desconocido'>()

  useEffect(() => {
    let vigente = true
    api.controlDeEnviosDelDespacho(anio, mes)
      .then((r) => { if (vigente) { setEnvios(r); setError('') } })
      .catch((e) => {
        if (vigente) setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron cargar los envíos del despacho.')
      })
    return () => { vigente = false }
  }, [anio, mes])

  useEffect(() => {
    let vigente = true
    setPlazos(undefined)
    api.plazosDelPeriodo(anio, mes)
      .then((r) => { if (vigente) setPlazos(plazosDelPeriodo(r)) })
      // El fallo del plazo no tumba la pantalla: la tabla de envíos es útil sin él, y el error
      // de la tabla ya tiene su propio lugar. Aquí basta con no inventar una fecha límite.
      .catch(() => { if (vigente) setPlazos('desconocido') })
    return () => { vigente = false }
  }, [anio, mes])

  if (error && !envios) return <p className="error" role="alert">{error}</p>
  if (!envios) return <p>Cargando…</p>

  const clientes = new Set(envios.map((e) => e.clienteId))
  const pendientes = envios.filter((e) => faltaAlgo(e.estado))
  const rechazados = envios.filter((e) => e.estado === 'Rechazado')
  const clientesConPendiente = new Set(pendientes.map((e) => e.clienteId))

  // El segmentado filtra la TABLA, no la franja de KPI ni los banners. La franja responde
  // «¿quién me falta este mes?», que es una pregunta del periodo completo: acotarla al grupo
  // activo daría un «sin acuse: 0» que solo significa «0 de este tipo», y un cero así miente.
  const grupoActivo = GRUPOS_DE_ARCHIVO.find((g) => g.clave === grupo)
  const visibles = grupoActivo ? envios.filter((e) => grupoActivo.tipos.includes(e.tipoArchivo)) : envios

  return (
    <>
      <header className="encabezado">
        <h1>Contabilidad electrónica</h1>
        <span>
          <select className="compacta" style={{ width: 'auto', margin: 0 }} value={mes}
            aria-label="Mes" onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((nombre, i) => <option key={nombre} value={i + 1}>{nombre}</option>)}
          </select>{' '}
          <select className="compacta" style={{ width: 'auto', margin: 0 }} value={anio}
            aria-label="Año" onChange={(e) => setAnio(Number(e.target.value))}>
            {[anio - 1, anio, anio + 1].filter((a, i, xs) => xs.indexOf(a) === i).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </span>
      </header>
      <p className="sutil">Anexo 24 v1.3 · {MESES[mes - 1]} {anio}</p>

      {/* A2 · La franja de plazos que la pantalla por cliente ya tenía. Aquí va un chip por tipo
          de persona, no por cliente: el plazo es el mismo para todas las morales de la cartera,
          y cae dos días hábiles antes que el de las físicas del MISMO periodo. */}
      {Array.isArray(plazos) && <FranjaDePlazos plazos={plazos} hoy={hoy} />}
      {plazos === 'desconocido' && (
        <p className="glosa">
          No se pudieron calcular los plazos de {MESES[mes - 1]} {anio}. Recarga la pantalla; no
          los deduzcas del mes, que el calendario oficial los corre por días inhábiles.
        </p>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      <div className="franja-kpi media tres">
        <div>
          <div className="kicker">Clientes con archivos</div>
          <div className="kpi-cifra">{clientes.size}</div>
          <div className="kpi-pie">{envios.length} {envios.length === 1 ? 'archivo generado' : 'archivos generados'}</div>
        </div>
        <div>
          <div className="kicker">Sin acuse</div>
          <div className={`kpi-cifra${pendientes.length > 0 ? ' alerta' : ''}`}>{pendientes.length}</div>
          <div className={`kpi-pie${pendientes.length > 0 ? ' alerta' : ''}`}>
            {clientesConPendiente.size === 0
              ? 'todo acusado'
              : `en ${clientesConPendiente.size} ${clientesConPendiente.size === 1 ? 'cliente' : 'clientes'}`}
          </div>
        </div>
        <div>
          <div className="kicker">Rechazados por el SAT</div>
          <div className={`kpi-cifra${rechazados.length > 0 ? ' alerta' : ''}`}>{rechazados.length}</div>
          <div className="kpi-pie">{rechazados.length > 0 ? 'hay que rehacerlos' : 'ninguno'}</div>
        </div>
      </div>

      {/* A3 · El prototipo pone aquí el banner de rechazo, con el nombre del cliente: en el
          cierre mensual el rechazo es lo primero que hay que atender, y hasta ahora solo se
          distinguía por el sombreado del renglón. Es el mismo componente de la pantalla por
          cliente, para que el rechazo se lea igual se mire desde donde se mire. */}
      {rechazados.map((e) => (
        <BannerDeRechazo
          key={e.id}
          clienteId={e.clienteId}
          control={e}
          sujeto={`${e.clienteRazonSocial} · ${NOMBRE_DE_TIPO_ARCHIVO[e.tipoArchivo]}`}
        />
      ))}

      <QueMeFaltaPresentar anio={anio} mes={mes} />

      <h2>Archivos generados del Anexo 24</h2>

      {envios.length === 0 ? (
        <p className="vacio">
          Nadie ha generado archivos del Anexo 24 para {MESES[mes - 1]} {anio}. Se generan desde el
          control de envíos de cada cliente.
        </p>
      ) : (
        <>
        <div className="encabezado" style={{ gap: 12, margin: '12px 0' }}>
          <div className="seg" role="radiogroup" aria-label="Tipo de archivo">
            <button type="button" className="seg-opcion" role="radio"
              aria-checked={grupoActivo === undefined} onClick={() => setGrupo(undefined)}>
              Todos · {envios.length}
            </button>
            {GRUPOS_DE_ARCHIVO.map((g) => (
              <button key={g.clave} type="button" className="seg-opcion" role="radio"
                aria-checked={grupoActivo?.clave === g.clave} onClick={() => setGrupo(g.clave)}>
                {g.etiqueta} · {envios.filter((e) => g.tipos.includes(e.tipoArchivo)).length}
              </button>
            ))}
          </div>
        </div>

        {visibles.length === 0 ? (
          <p className="vacio">
            Ningún archivo de tipo «{grupoActivo!.etiqueta}» en {MESES[mes - 1]} {anio}. Los demás
            grupos sí tienen archivos: cámbialo arriba para verlos.
          </p>
        ) : (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Archivo</th>
                <th style={{ whiteSpace: 'nowrap' }}>Archivo (RN-07)</th>
                <th>XSD</th>
                <th>Estado</th>
                <th>Generado</th>
                <th>Enviado</th>
                <th>Acuse</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((e) => (
                <tr key={e.id} className={e.estado === 'Rechazado' ? 'resaltada' : undefined}>
                  <td>
                    <Link to={`/clientes/${e.clienteId}/control-envios`}>{e.clienteRazonSocial}</Link>{' '}
                    <span className="sutil mono">{e.clienteRfc}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{NOMBRE_DE_TIPO_ARCHIVO[e.tipoArchivo]}</td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {e.nombreDeArchivo ?? <span className="sutil">—</span>}
                  </td>
                  <td><SelloXsd control={e} /></td>
                  <td>
                    <span className={`insignia ${INSIGNIA_DE_ESTADO[e.estado]}`}>{e.estado}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fecha(e.generadoEnUtc)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fecha(e.enviadoEnUtc)}</td>
                  <td>
                    {e.estado === 'Rechazado'
                      ? <span className="sutil">{e.motivoRechazo ?? 'rechazado sin motivo registrado'}</span>
                      : e.folioAcuse
                        ? <span className="mono">{e.folioAcuse}</span>
                        : <span className="sutil">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}

      <p className="nota">
        El envío por el Buzón Tributario es manual: el contador genera el archivo en el control de
        envíos del cliente, lo sube al SAT y registra ahí el acuse o el rechazo (RF-805). Las
        declaraciones se presentan en el portal del SAT y se registran en la pestaña Declaraciones del
        cliente. ContaQuick no presenta nada por ti (RN-62). Esta vista solo muestra los clientes de tu
        cartera.
      </p>
    </>
  )
}
